import { forwardRef, useCallback, useImperativeHandle, useRef } from 'react';
import { Turnstile } from '@marsidev/react-turnstile';
import { logger } from '../../helpers/logger.helper';
import { TURNSTILE_SITE_KEY } from './turnstile.constants';
import type { TurnstileWidgetProps, TurnstileWidgetRef } from './TurnstileWidget.types';

interface TurnstileComponentRef {
  execute: () => void;
  reset: () => void;
}

export const TurnstileWidget = forwardRef<TurnstileWidgetRef, TurnstileWidgetProps>(function TurnstileWidget(
  { mode = 'invisible' },
  ref,
) {
  const turnstileRef = useRef<TurnstileComponentRef | null>(null);
  const resolverRef = useRef<((token: string) => void) | null>(null);
  const rejecterRef = useRef<((reason?: unknown) => void) | null>(null);
  const isReadyRef = useRef(false);

  const clearPending = useCallback(() => {
    resolverRef.current = null;
    rejecterRef.current = null;
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      execute: () =>
        new Promise<string>((resolve, reject) => {
          if (!turnstileRef.current || !TURNSTILE_SITE_KEY) {
            logger.error('Turnstile execute rejected: unavailable', {
              hasTurnstileRef: Boolean(turnstileRef.current),
              hasSiteKey: Boolean(TURNSTILE_SITE_KEY),
            });
            reject(new Error('Turnstile is unavailable'));
            return;
          }
          resolverRef.current = resolve;
          rejecterRef.current = reject;
          if (isReadyRef.current) {
            turnstileRef.current.execute();
          }
          // else: onWidgetLoad will trigger execute() once the widget is ready
        }),
      reset: () => {
        if (!turnstileRef.current) return;
        clearPending();
        turnstileRef.current.reset();
      },
    }),
    [clearPending]
  );

  if (!TURNSTILE_SITE_KEY) {
    return null;
  }

  return (
    <Turnstile
      key={mode}
      ref={turnstileRef}
      siteKey={TURNSTILE_SITE_KEY}
      options={{ execution: 'execute', size: mode === 'visible' ? 'normal' : 'invisible' }}
      onWidgetLoad={(widgetId: string) => {
        isReadyRef.current = true;
        logger.debug('Turnstile web widget loaded', { widgetId });
        try {
          if (resolverRef.current && turnstileRef.current) {
            turnstileRef.current.execute();
          }
        } catch (err) {
          rejecterRef.current?.(err);
          clearPending();
        }
      }}
      onSuccess={(token: string) => {
        logger.debug('Turnstile web success token received', { tokenLength: token.length });
        resolverRef.current?.(token);
        clearPending();
      }}
      onError={() => {
        logger.error('Turnstile web onError callback fired');
        rejecterRef.current?.(new Error('Turnstile challenge failed'));
        clearPending();
      }}
      onExpire={() => {
        logger.warn('Turnstile web token expired');
        rejecterRef.current?.(new Error('Turnstile token expired'));
        clearPending();
      }}
    />
  );
});

